import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { ProductCard, ProductCardProps } from '@components/molecules/ProductCard';
import { RoundIcon, RoundIconVariants } from '@components/atoms/RoundIcon';
import { Container } from '@components/atoms/Container';
import { useMediaQuery } from '@hooks/useMediaQuery';
import { ProductsHorizontalSlider } from '../horizontalSliders/ProductsHorizontalSlider';
import { SectionHeader, SectionHeaderProps } from '../SectionHeader';
import styles from './ProductsHighlightsSlider.module.css';

export interface ProductsHighlightsSliderProps extends Omit<SectionHeaderProps, 'title'> {
    title: string;
    products: ProductCardProps[];
    editMode?: boolean;
}

const ITEMS_PER_SLIDE = 4;

export const ProductsHighlightsSlider: FC<ProductsHighlightsSliderProps> = ({
    title,
    actionLabel,
    linkTo,
    products,
    editMode,
}) => {
    const pageSize = useMediaQuery();
    const { t } = useTranslation();

    const translations = {
        previous: t('products-highlights-slider-component.previous'),
        next: t('products-highlights-slider-component.next'),
    };

    const slides = Array.from(Array(Math.ceil(products.length / ITEMS_PER_SLIDE)).keys());

    return (
        <div data-testid="products-highlights-slider">
            <Container classes="px-0" customSize="custom-large">
                <div className="position-relative">
                    {editMode ? (
                        <>
                            <SectionHeader title={{ text: title }} />
                            <div className="position-absolute end-0 top-0">
                                <RoundIcon variant={RoundIconVariants.variant2} icon="fa-light fa-pen-to-square" />
                            </div>
                        </>
                    ) : (
                        <SectionHeader title={{ text: title }} actionLabel={actionLabel} linkTo={linkTo} />
                    )}
                </div>
            </Container>

            {/* list of products */}
            {['xs', 'sm'].includes(pageSize) ? (
                <ProductsHorizontalSlider products={products} />
            ) : (
                <Container classes="px-0" customSize="custom-large">
                    <div id="productsCarousel" className={`${styles['products-carousel']} d-none d-md-block carousel slide`}>
                        <div className="carousel-inner">
                            {slides.map((slide) => (
                                <div key={`products-slider-${slide}`} className={`carousel-item ${slide === 0 ? 'active' : ''}`}>
                                    <div className="row">
                                        {products
                                            .slice(slide * ITEMS_PER_SLIDE, slide * ITEMS_PER_SLIDE + ITEMS_PER_SLIDE)
                                            .map((product, productIndex) => (
                                                <div key={`product-${productIndex}`} className="col-3">
                                                    <ProductCard {...product} />
                                                </div>
                                            ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                        {/* controls */}
                        {!editMode && (
                            <>
                                <button
                                    className={`carousel-control-prev ${styles['carousel-control-prev']} hide-on-edit-mode`}
                                    type="button"
                                    data-bs-target="#productsCarousel"
                                    data-bs-slide="prev">
                                    <span
                                        className={`carousel-control-prev-icon ${styles['carousel-control-prev-icon']}`}
                                        aria-hidden="true">
                                        <i className="fa-light fa-arrow-left"></i>
                                    </span>
                                    <span className="visually-hidden">{translations.previous}</span>
                                </button>
                                <button
                                    className={`carousel-control-next ${styles['carousel-control-next']} hide-on-edit-mode`}
                                    type="button"
                                    data-bs-target="#productsCarousel"
                                    data-bs-slide="next">
                                    <span
                                        className={`carousel-control-next-icon ${styles['carousel-control-next-icon']}`}
                                        aria-hidden="true">
                                        <i className="fa-light fa-arrow-right"></i>
                                    </span>
                                    <span className="visually-hidden">{translations.next}</span>
                                </button>
                            </>
                        )}
                    </div>
                </Container>
            )}
        </div>
    );
};
